import { useMemo, useState } from 'react'
import MultiSelectFilter from '@/components/MultiSelectFilter'
import {
  collectStoresFromPeriodFlow,
  drawMetric,
  filterPeriodFlowRows,
  flowMetric,
  sortPeriodFlowRows,
  type FlowMode,
  type FlowPeriodSkuRow,
  type FlowRowSort,
  type FlowScope,
  type FlowSkuAmount,
  type FlowStoreDraw,
} from '@/utils/ledgerFlow'

const SORT_OPTIONS: { id: FlowRowSort; label: string }[] = [
  { id: 'activity', label: 'Activity' },
  { id: 'name', label: 'Name' },
  { id: 'ending', label: 'Ending' },
]

type WaterfallStoreColumn = 'Highland Park' | 'River Oaks' | 'Unknown store'

type WaterfallStep =
  | { kind: 'opening'; label: string; amount: FlowSkuAmount }
  | { kind: 'purchases'; label: string; amount: FlowSkuAmount }
  | { kind: 'adjustment'; label: string; amount: FlowSkuAmount }
  | { kind: 'draw'; label: string; draw: FlowStoreDraw }
  | { kind: 'ending'; label: string; amount: FlowSkuAmount }

type WarehouseInventoryWaterfallProps = {
  rows: FlowPeriodSkuRow[]
  mode: FlowMode
  scope: FlowScope
  productNames: Map<string, string>
  highlightedSku?: string | null
  onHighlightSku?: (sku: string | null) => void
  searchQuery?: string
  onSearchQueryChange?: (query: string) => void
}

function formatMoney(value: number): string {
  return value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })
}

function formatQty(value: number): string {
  return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

function formatMetric(value: number, mode: FlowMode): string {
  return mode === 'qty' ? `${formatQty(value)} cases` : formatMoney(value)
}

function formatSignedMetric(value: number, mode: FlowMode): string {
  const abs = Math.abs(value)
  const body = mode === 'qty' ? `${formatQty(abs)} cases` : formatMoney(abs)
  if (value > 0) return `+${body}`
  if (value < 0) return `−${body}`
  return mode === 'qty' ? '0 cases' : formatMoney(0)
}

function skuDisplayLabel(
  bulkSku: string,
  productNames: Map<string, string>
): string {
  const name = productNames.get(bulkSku)?.trim()
  return name ? `${name} (${bulkSku})` : bulkSku
}

function normalizeWaterfallStore(destination: string): WaterfallStoreColumn {
  const trimmed = destination.trim()
  if (trimmed === 'Highland Park' || trimmed === 'River Oaks') return trimmed
  return 'Unknown store'
}

function drawForStore(
  storeDraws: FlowStoreDraw[],
  store: WaterfallStoreColumn
): FlowStoreDraw {
  let qty = 0
  let value = 0
  for (const draw of storeDraws) {
    if (normalizeWaterfallStore(draw.destination) !== store) continue
    qty += draw.qty
    value += draw.value
  }
  return { destination: store, qty, value }
}

/**
 * Fixed 7 columns for every SKU waterfall:
 * Opening · Purchases · Adjustments · Highland Park · River Oaks · Unknown store · Ending
 */
function buildWaterfallSteps(row: FlowPeriodSkuRow): WaterfallStep[] {
  return [
    { kind: 'opening', label: 'Opening', amount: row.opening },
    { kind: 'purchases', label: 'Purchases', amount: row.purchases },
    { kind: 'adjustment', label: 'Adjustments', amount: row.adjustments },
    {
      kind: 'draw',
      label: 'Highland Park',
      draw: drawForStore(row.storeDraws, 'Highland Park'),
    },
    {
      kind: 'draw',
      label: 'River Oaks',
      draw: drawForStore(row.storeDraws, 'River Oaks'),
    },
    {
      kind: 'draw',
      label: 'Unknown store',
      draw: drawForStore(row.storeDraws, 'Unknown store'),
    },
    { kind: 'ending', label: 'Ending', amount: row.ending },
  ]
}

/** Absolute magnitude used for bar height. */
function stepAbsMetric(step: WaterfallStep, mode: FlowMode): number {
  if (step.kind === 'draw') return drawMetric(step.draw, mode)
  return Math.abs(flowMetric(step.amount, mode))
}

/** Signed delta applied to the running total (+ in, − out). */
function stepSignedDelta(step: WaterfallStep, mode: FlowMode): number {
  if (step.kind === 'draw') return -drawMetric(step.draw, mode)
  if (step.kind === 'adjustment') return flowMetric(step.amount, mode)
  if (step.kind === 'ending') return 0
  return flowMetric(step.amount, mode)
}

function stepColor(step: WaterfallStep, mode: FlowMode): string {
  switch (step.kind) {
    case 'opening':
      return '#94a3b8'
    case 'purchases':
      return '#10b981'
    case 'adjustment': {
      const signed = flowMetric(step.amount, mode)
      if (signed > 0) return '#10b981'
      if (signed < 0) return '#f87171'
      return '#94a3b8'
    }
    case 'draw':
      return '#f87171'
    case 'ending':
      return '#3b82f6'
  }
}

/** Chart plot height per SKU waterfall (px). Tall enough for floating bridge bars. */
const WATERFALL_PLOT_HEIGHT = 168
const WATERFALL_COLUMN_GAP_PCT = 12
/** Visual height for zero-value stub columns (px). */
const ZERO_STUB_HEIGHT_PX = 4

type WaterfallColumn = {
  step: WaterfallStep
  index: number
  /** Absolute magnitude at bottom of bar */
  y0: number
  /** Absolute magnitude at top of bar */
  y1: number
  /** Running total after this step */
  runningAfter: number
  isZero: boolean
}

function buildWaterfallColumns(
  steps: WaterfallStep[],
  mode: FlowMode
): { columns: WaterfallColumn[]; peak: number } {
  const columns: WaterfallColumn[] = []
  let running = 0
  let peak = 0

  for (let index = 0; index < steps.length; index++) {
    const step = steps[index]!

    if (step.kind === 'ending') {
      const value = Math.max(0, flowMetric(step.amount, mode))
      if (value === 0) {
        columns.push({
          step,
          index,
          y0: 0,
          y1: 0,
          runningAfter: 0,
          isZero: true,
        })
        running = 0
        continue
      }
      columns.push({
        step,
        index,
        y0: 0,
        y1: value,
        runningAfter: value,
        isZero: false,
      })
      running = value
      peak = Math.max(peak, value)
      continue
    }

    const signed = stepSignedDelta(step, mode)
    const abs = Math.abs(signed)

    if (abs === 0) {
      columns.push({
        step,
        index,
        y0: running,
        y1: running,
        runningAfter: running,
        isZero: true,
      })
      peak = Math.max(peak, running)
      continue
    }

    if (signed > 0) {
      const y0 = running
      const y1 = running + abs
      running = y1
      columns.push({ step, index, y0, y1, runningAfter: running, isZero: false })
      peak = Math.max(peak, y1)
    } else {
      const y1 = running
      const y0 = Math.max(0, running - abs)
      running = y0
      columns.push({ step, index, y0, y1, runningAfter: running, isZero: false })
      peak = Math.max(peak, y1)
    }
  }

  return { columns, peak: peak || 1 }
}

function WaterfallSkuRow({
  row,
  mode,
  productNames,
  highlighted,
  onHighlight,
}: {
  row: FlowPeriodSkuRow
  mode: FlowMode
  productNames: Map<string, string>
  highlighted: boolean
  onHighlight: (sku: string | null) => void
}) {
  const [hoveredStep, setHoveredStep] = useState<number | null>(null)
  const steps = useMemo(() => buildWaterfallSteps(row), [row])
  const { columns, peak } = useMemo(() => buildWaterfallColumns(steps, mode), [mode, steps])

  const label = skuDisplayLabel(row.bulk_sku, productNames)
  const hovered = hoveredStep != null ? columns.find((c) => c.index === hoveredStep) : null
  const columnCount = Math.max(columns.length, 1)
  const slotPct = 100 / columnCount
  const barWidthPct = Math.max(slotPct * (1 - WATERFALL_COLUMN_GAP_PCT / 100), 4)

  function stepHoverText(step: WaterfallStep): string {
    if (step.kind === 'draw') {
      return `${step.label}: ${formatMetric(drawMetric(step.draw, mode), mode)}`
    }
    if (step.kind === 'adjustment') {
      return `${step.label}: ${formatSignedMetric(flowMetric(step.amount, mode), mode)}`
    }
    return `${step.label}: ${formatMetric(flowMetric(step.amount, mode), mode)}`
  }

  function stepTitle(step: WaterfallStep): string {
    return stepHoverText(step)
  }

  function stepPrefix(step: WaterfallStep): string {
    if (step.kind === 'purchases') return '+'
    if (step.kind === 'adjustment') {
      const signed = flowMetric(step.amount, mode)
      if (signed > 0) return '+'
      if (signed < 0) return '−'
      return ''
    }
    return ''
  }

  return (
    <div
      className={`rounded-lg border px-3 py-3 transition-colors ${
        highlighted
          ? 'border-gray-900 bg-white shadow-sm'
          : 'border-gray-200 bg-gray-50/50 hover:border-gray-300'
      }`}
      onMouseEnter={() => onHighlight(row.bulk_sku)}
      onMouseLeave={() => onHighlight(null)}
    >
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="truncate text-xs font-semibold text-gray-900" title={label}>
          {label}
        </p>
        {hovered ? (
          <p className="shrink-0 text-[10px] text-gray-600">{stepHoverText(hovered.step)}</p>
        ) : null}
      </div>

      {/* Classic bridge: X = step columns, Y = magnitude */}
      <div
        className="relative w-full rounded-md border border-gray-100 bg-white"
        style={{ height: WATERFALL_PLOT_HEIGHT }}
      >
        {/* Baseline */}
        <div className="pointer-events-none absolute inset-x-2 bottom-0 border-t border-gray-200" />

        {/* Connector lines between successive column tops/bottoms */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          aria-hidden="true"
        >
          {columns.slice(0, -1).map((column, i) => {
            const x1 = ((i + 0.5) / columnCount) * 100
            const x2 = ((i + 1.5) / columnCount) * 100
            const yLevel = column.runningAfter
            const yPct = 100 - (yLevel / peak) * 100
            return (
              <line
                key={`conn-${row.bulk_sku}-${i}`}
                x1={`${x1}%`}
                x2={`${x2}%`}
                y1={`${yPct}%`}
                y2={`${yPct}%`}
                stroke="#cbd5e1"
                strokeWidth={1}
                strokeDasharray="3 3"
              />
            )
          })}
        </svg>

        {columns.map((column, colIndex) => {
          const absValue = stepAbsMetric(column.step, mode)
          const leftPct = colIndex * slotPct + (slotPct - barWidthPct) / 2
          const title = stepTitle(column.step)
          const prefix = stepPrefix(column.step)
          const color = stepColor(column.step, mode)

          if (column.isZero) {
            const stubTopPx =
              WATERFALL_PLOT_HEIGHT -
              (column.runningAfter / peak) * WATERFALL_PLOT_HEIGHT -
              ZERO_STUB_HEIGHT_PX / 2
            return (
              <div
                key={`${row.bulk_sku}-${column.index}`}
                className="absolute rounded-sm transition-opacity"
                style={{
                  left: `${leftPct}%`,
                  width: `${barWidthPct}%`,
                  top: `${Math.max(0, stubTopPx)}px`,
                  height: `${ZERO_STUB_HEIGHT_PX}px`,
                  backgroundColor: color,
                  opacity: hoveredStep == null || hoveredStep === column.index ? 0.85 : 0.35,
                }}
                title={title}
                onMouseEnter={() => setHoveredStep(column.index)}
                onMouseLeave={() => setHoveredStep(null)}
              />
            )
          }

          const heightPct = ((column.y1 - column.y0) / peak) * 100
          const topPct = 100 - (column.y1 / peak) * 100
          const showValueLabel = heightPct >= 14

          return (
            <div
              key={`${row.bulk_sku}-${column.index}`}
              className="absolute flex flex-col items-center justify-end rounded-sm transition-opacity"
              style={{
                left: `${leftPct}%`,
                width: `${barWidthPct}%`,
                top: `${topPct}%`,
                height: `${Math.max(heightPct, 1.5)}%`,
                backgroundColor: color,
                opacity: hoveredStep == null || hoveredStep === column.index ? 0.92 : 0.35,
              }}
              title={title}
              onMouseEnter={() => setHoveredStep(column.index)}
              onMouseLeave={() => setHoveredStep(null)}
            >
              {showValueLabel ? (
                <span className="truncate px-0.5 pb-0.5 text-center text-[10px] font-semibold leading-tight text-white">
                  {prefix}
                  {mode === 'qty' ? formatQty(absValue) : formatMoney(absValue)}
                </span>
              ) : null}
            </div>
          )
        })}
      </div>

      {/* Column labels under the plot */}
      <div className="mt-1.5 flex w-full">
        {columns.map((column) => {
          const labelValue =
            column.step.kind === 'draw'
              ? drawMetric(column.step.draw, mode)
              : flowMetric(column.step.amount, mode)
          const labelText =
            column.step.kind === 'adjustment'
              ? formatSignedMetric(labelValue, mode)
              : column.step.kind === 'purchases' && labelValue > 0
                ? `+${formatMetric(labelValue, mode)}`
                : formatMetric(Math.abs(labelValue), mode)

          return (
            <div
              key={`label-${row.bulk_sku}-${column.index}`}
              className="min-w-0 px-0.5 text-center"
              style={{ width: `${slotPct}%` }}
            >
              <p
                className="truncate text-[10px] font-medium text-gray-600"
                title={column.step.label}
              >
                {column.step.label}
              </p>
              <p className="truncate text-[10px] text-gray-400">{labelText}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function WarehouseInventoryWaterfall({
  rows,
  mode,
  scope,
  productNames,
  highlightedSku,
  onHighlightSku,
  searchQuery: controlledSearch,
  onSearchQueryChange,
}: WarehouseInventoryWaterfallProps) {
  const [internalSearch, setInternalSearch] = useState('')
  const [sort, setSort] = useState<FlowRowSort>('activity')
  const [selectedStores, setSelectedStores] = useState<string[]>([])

  const searchQuery = controlledSearch ?? internalSearch
  const setSearchQuery = onSearchQueryChange ?? setInternalSearch

  const storeOptions = useMemo(
    () =>
      collectStoresFromPeriodFlow(rows).map((store) => ({
        id: store,
        label: store,
      })),
    [rows]
  )

  const filteredRows = useMemo(() => {
    const filtered = filterPeriodFlowRows(
      rows,
      { search: searchQuery, stores: selectedStores, mode },
      productNames
    )
    return sortPeriodFlowRows(filtered, sort, mode, productNames)
  }, [mode, productNames, rows, searchQuery, selectedStores, sort])

  function handleSortChange(selected: string[]) {
    if (selected.length === 0) return
    if (selected.length === 1) {
      const only = selected[0]
      if (only === 'activity' || only === 'name' || only === 'ending') {
        setSort(only)
      }
      return
    }
    const added = selected.find((id) => id !== sort)
    if (added === 'activity' || added === 'name' || added === 'ending') {
      setSort(added)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="filter-bar flex shrink-0 flex-nowrap items-end gap-3 overflow-x-auto rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2">
        <div className="flex min-w-[10rem] flex-1 flex-col gap-1">
          <span className="block h-4 truncate text-xs font-medium leading-4 text-gray-600">
            Search
          </span>
          <input
            type="text"
            role="searchbox"
            className="input h-8 text-xs"
            placeholder="SKU or product name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search SKUs"
          />
        </div>

        <div className="w-40 shrink-0">
          <MultiSelectFilter
            id="waterfall-filter-sort"
            label="Sort"
            options={SORT_OPTIONS}
            selected={[sort]}
            onChange={handleSortChange}
            placeholder="Activity"
            searchable={false}
          />
        </div>

        {storeOptions.length > 0 ? (
          <div className="w-48 shrink-0">
            <MultiSelectFilter
              id="waterfall-filter-store"
              label="Store"
              options={storeOptions}
              selected={selectedStores}
              onChange={setSelectedStores}
              placeholder="All stores"
            />
          </div>
        ) : null}
      </div>

      <p className="shrink-0 text-xs text-gray-500">
        {filteredRows.length} SKU{filteredRows.length === 1 ? '' : 's'}
        {scope === 'cumulative' ? ' · cumulative (no opening step)' : ' · single month'}
        {mode === 'qty' ? ' · cases' : ' · dollars'}
      </p>

      {filteredRows.length === 0 ? (
        <p className="text-sm text-gray-500">No SKUs match the current filters.</p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto pr-1">
          {filteredRows.map((row) => (
            <WaterfallSkuRow
              key={row.bulk_sku}
              row={row}
              mode={mode}
              productNames={productNames}
              highlighted={highlightedSku === row.bulk_sku}
              onHighlight={onHighlightSku ?? (() => {})}
            />
          ))}
        </div>
      )}
    </div>
  )
}
